import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin, Globe, Phone, Mail, MessageCircle, Calendar, Building2,
  Instagram, Linkedin, Facebook, CheckCircle2,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import GalleryGrid from '../components/profile/GalleryGrid';
import { api, friendlyError } from '../lib/api';
import { track, EventTypes } from '../lib/analytics';

export default function DeveloperProfile() {
  const { slug } = useParams();
  const [dev, setDev] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getDeveloper(slug),
      api.listProjects({ limit: 24 }),
    ])
      .then(([d, p]) => {
        setDev(d);
        setProjects((p.items || []).filter(it => it.developer_slug === d.slug));
        if (d?.id) track(EventTypes.DEVELOPER_PROFILE_VIEWED, { developer_id: d.id });
      })
      .catch((e) => setErr(friendlyError(e, 'No pudimos cargar la constructora.')))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin" /></div>;
  if (err || !dev) return (<><Header /><div className="max-w-xl mx-auto p-12 text-center" data-testid="developer-not-found"><p>{err || 'Constructora no encontrada'}</p></div><Footer /></>);

  const whatsappLink = dev.whatsapp ? `https://wa.me/${String(dev.whatsapp).replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${dev.name}, vi su perfil en habita.co y me interesa conocer sus proyectos.`)}` : null;
  const yearsExperience = dev.founded_year ? new Date().getFullYear() - dev.founded_year : null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: dev.name,
    description: dev.description,
    image: dev.logo_url,
    url: dev.website,
    foundingDate: dev.founded_year ? String(dev.founded_year) : undefined,
    telephone: dev.phone,
    email: dev.email,
    address: { '@type': 'PostalAddress', addressLocality: dev.city, addressCountry: dev.country || 'CO' },
  };

  return (
    <div className="min-h-screen bg-habita-cream overflow-x-hidden">
      <SEO
        title={`${dev.name} · Constructora`}
        description={dev.description || `${dev.name} es una constructora con presencia en ${dev.country || 'Colombia'} y proyectos inmobiliarios activos.`}
        image={dev.cover_url || dev.logo_url}
        jsonLd={jsonLd}
      />
      <Header />

      {/* HERO */}
      <div className="relative" data-testid="developer-hero">
        <div className="h-48 md:h-64 lg:h-80 overflow-hidden bg-habita-dark">
          {dev.cover_url
            ? <img src={dev.cover_url} alt="" className="w-full h-full object-cover opacity-90" />
            : <div className="w-full h-full bg-gradient-to-br from-habita-dark to-habita" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        </div>

        <div className="max-w-6xl mx-auto px-4 lg:px-8 -mt-16 lg:-mt-20 relative">
          <div className="flex flex-col md:flex-row md:items-end gap-5">
            <div className="w-28 h-28 lg:w-36 lg:h-36 rounded-2xl bg-white border-4 border-white shadow-xl overflow-hidden shrink-0">
              {dev.logo_url
                ? <img src={dev.logo_url} alt={dev.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-habita-mint flex items-center justify-center text-habita"><Building2 className="w-12 h-12" /></div>}
            </div>
            <div className="flex-1 min-w-0 pb-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-serif text-2xl lg:text-4xl font-bold text-habita-dark break-words" data-testid="developer-name">{dev.name}</h1>
                {dev.verified && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold" data-testid="developer-verified-badge">
                    <CheckCircle2 className="w-3.5 h-3.5" /> CONSTRUCTORA VERIFICADA
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                {dev.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {dev.city}</span>}
                {yearsExperience != null && yearsExperience > 0 && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {yearsExperience} años de experiencia</span>}
                {dev.projects_count != null && <span>· {dev.projects_count} proyectos</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pb-2 shrink-0">
              {whatsappLink && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-habita inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold"
                  data-testid="developer-whatsapp-btn"
                  onClick={() => track('developer.whatsapp_clicked', { developer_id: dev.id })}
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              )}
              {dev.website && (
                <a
                  href={dev.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold border border-gray-300 hover:border-habita bg-white"
                  data-testid="developer-website-btn"
                  onClick={() => track('developer.website_clicked', { developer_id: dev.id })}
                >
                  <Globe className="w-4 h-4" /> Web
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <main className="lg:col-span-2 space-y-6">
          {/* About */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="developer-about-card">
            <h2 className="font-serif text-xl font-bold text-habita-dark">Sobre la constructora</h2>
            <p className="text-sm text-gray-700 mt-3 whitespace-pre-line leading-relaxed">{dev.description || 'Sin descripción disponible.'}</p>

            {dev.cities_operating?.length > 0 && (
              <div className="mt-5">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Ciudades donde opera</p>
                <div className="flex flex-wrap gap-2" data-testid="developer-cities-operating">
                  {dev.cities_operating.map(c => (
                    <span key={c} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-habita-cream border border-habita/20 text-habita-dark text-xs font-semibold">
                      <MapPin className="w-3 h-3 text-habita" /> {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {dev.countries_operating?.length > 0 && (
              <div className="mt-5" data-testid="developer-countries-operating">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Países donde opera</p>
                <div className="flex flex-wrap gap-2">
                  {dev.countries_operating.map(c => (
                    <span key={c} className="px-3 py-1.5 rounded-full bg-habita-mint text-habita text-xs font-bold uppercase">{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {dev.gallery?.length > 0 && (
            <GalleryGrid images={dev.gallery} title="Galería" testid="developer-gallery" altPrefix={`${dev.name} galería`} />
          )}

          {/* Projects */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="developer-projects-section">
            <h2 className="font-serif text-xl font-bold text-habita-dark mb-4">Proyectos activos ({projects.length})</h2>
            {projects.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">Aún no hay proyectos publicados.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {projects.map(p => (
                  <Link
                    key={p.id}
                    to={`/proyecto/${p.slug}`}
                    onClick={() => track('developer.project_clicked', { developer_id: dev.id, project_id: p.id })}
                    className="group block bg-habita-cream rounded-xl overflow-hidden border border-transparent hover:border-habita transition-all"
                    data-testid={`developer-project-card-${p.id}`}
                  >
                    <div className="h-32 bg-habita-mint relative">
                      {p.cover_url
                        ? <img src={p.cover_url} alt={p.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full bg-gradient-to-br from-habita to-emerald-600" />}
                      {p.status && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-white/95 text-habita-dark text-[10px] font-bold uppercase">{p.status.replace('_', ' ')}</span>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="font-serif font-bold text-habita-dark group-hover:text-habita transition-colors truncate">{p.name}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {p.city}, {p.country}</p>
                      {(p.price_min != null) && (
                        <p className="text-xs text-habita font-semibold mt-2">
                          Desde {p.currency} {p.price_min.toLocaleString('es-CO')}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </main>

        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="developer-contact-card">
            <h3 className="font-serif text-base font-bold text-habita-dark mb-3">Contacto</h3>
            <div className="space-y-2.5 text-sm">
              {dev.phone && <div className="flex items-center gap-2 text-gray-700"><Phone className="w-4 h-4 text-habita" /><a href={`tel:${dev.phone}`}>{dev.phone}</a></div>}
              {dev.email && <div className="flex items-center gap-2 text-gray-700"><Mail className="w-4 h-4 text-habita" /><a href={`mailto:${dev.email}`} className="break-all">{dev.email}</a></div>}
              {dev.website && <div className="flex items-center gap-2 text-gray-700"><Globe className="w-4 h-4 text-habita" /><a href={dev.website} target="_blank" rel="noopener noreferrer" className="truncate">{dev.website.replace(/^https?:\/\//, '')}</a></div>}
              {whatsappLink && (
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-habita w-full inline-flex items-center justify-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold mt-2" data-testid="developer-cta-contact">
                  <MessageCircle className="w-4 h-4" /> Hablar con la constructora
                </a>
              )}
            </div>
          </div>

          {dev.social && Object.keys(dev.social).filter(k => dev.social[k]).length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="developer-social-card">
              <h3 className="font-serif text-base font-bold text-habita-dark mb-3">Síguenos</h3>
              <div className="flex gap-2">
                {dev.social.instagram && <a href={dev.social.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-habita-mint flex items-center justify-center text-habita hover:bg-habita hover:text-white transition-colors"><Instagram className="w-4 h-4" /></a>}
                {dev.social.linkedin && <a href={dev.social.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-habita-mint flex items-center justify-center text-habita hover:bg-habita hover:text-white transition-colors"><Linkedin className="w-4 h-4" /></a>}
                {dev.social.facebook && <a href={dev.social.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-habita-mint flex items-center justify-center text-habita hover:bg-habita hover:text-white transition-colors"><Facebook className="w-4 h-4" /></a>}
              </div>
            </div>
          )}
        </aside>
      </div>

      <Footer />
    </div>
  );
}
